import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays, Briefcase, CalendarOff, Users, LogOut, Menu, X, Clock, CheckSquare, Tags,
  CreditCard, ShieldCheck, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, isAdmin, isLeader, isSuperAdmin, roleLabel } from "@/lib/api";
import { currentLocale } from "@/i18n";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings as SettingsIcon } from "lucide-react";
import CustomerButton from "@/components/CustomerButton";
import HeartButton from "@/components/HeartButton";

const MAIN_NAV = [
  { to: "/kalender", key: "calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/jobb", key: "jobs", icon: Briefcase, testid: "nav-jobs" },
];

const PERSONNEL_NAV = [
  { to: "/personal", key: "staffOverview", icon: Users, testid: "nav-staff" },
  { to: "/godkannande", key: "approvals", icon: CheckSquare, testid: "nav-approvals", badge: "approvals" },
  { to: "/franvaro", key: "absences", icon: CalendarOff, testid: "nav-absences", badge: "absences" },
  { to: "/abonnemang", key: "settings", icon: CreditCard, testid: "nav-subscription", adminOnly: true },
];

const TAIL_NAV = [
  { to: "/tidrapport", key: "reports", icon: Clock, testid: "nav-reports", leaderOnly: true },
  { to: "/prislista", key: "pricelist", icon: Tags, testid: "nav-pricelist" },
  { to: "/installningar", key: "accountSettings", icon: SettingsIcon, testid: "nav-settings" },
];

const Badge = ({ count, testid }) =>
  count > 0 ? (
    <span
      data-testid={testid}
      className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white"
    >
      {count}
    </span>
  ) : null;

export const Layout = () => {
  const { t } = useTranslation();
  const { user, logout, company } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [heartMsg, setHeartMsg] = useState("");
  const [badges, setBadges] = useState({ absences: 0, approvals: 0 });

  const loadBadges = useCallback(async () => {
    try {
      const { data } = await api.get("/nav/badges");
      setBadges({ absences: data.absences || 0, approvals: data.approvals || 0 });
    } catch (e) {
      console.error("Kunde inte hämta navigationsmärken", e);
    }
  }, []);

  useEffect(() => {
    loadBadges();
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [loadBadges]);

  // Öppnad sida = läst: nollar siffran för den vyn
  useEffect(() => {
    const key =
      location.pathname.startsWith("/franvaro") ? "absences" :
      location.pathname.startsWith("/godkannande") ? "approvals" : null;
    if (!key) {
      loadBadges();
      return;
    }
    api.post(`/nav/seen/${key}`).then(loadBadges).catch(() => {});
  }, [location.pathname, loadBadges]);

  const doLogout = () => {
    logout();
    navigate("/logga-in");
  };

  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  const allowed = (n) =>
    (!n.leaderOnly || isLeader(user?.role)) &&
    (!n.adminOnly || isAdmin(user?.role)) &&
    (!n.superOnly || isSuperAdmin(user?.role));
  const personnel = PERSONNEL_NAV.filter(allowed);
  const tail = TAIL_NAV.filter(allowed);
  const badgeCount = { approvals: badges.approvals, absences: badges.absences };
  const personnelBadge = personnel.reduce((s, n) => s + (n.badge ? badgeCount[n.badge] || 0 : 0), 0);
  const personnelActive = personnel.some((n) => location.pathname.startsWith(n.to));
  const today = new Date().toLocaleDateString(currentLocale());

  return (
    <div className="min-h-screen bg-amber-50/40">
      <header className="sticky top-0 z-40 border-b border-amber-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex h-14 w-full max-w-[1800px] items-center gap-3 overflow-hidden px-4 lg:px-8">
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 font-heading text-sm font-bold text-white">
              FM
            </div>
            <span className="font-heading text-base font-bold text-slate-900">FältMästare</span>
            <span className="hidden whitespace-nowrap text-sm font-medium text-slate-500 2xl:inline" data-testid="header-date">
              {today}
            </span>
          </div>

          <nav className="ml-3 hidden min-w-0 shrink items-center gap-0.5 overflow-x-auto lg:flex">
            {MAIN_NAV.map(({ to, key, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(`nav.${key}`)}
              </NavLink>
            ))}

            {personnel.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-personnel"
                    className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-sm font-medium transition-colors duration-200 ${
                      personnelActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <Users className="h-4 w-4 shrink-0" />
                    {t("nav.personnel")}
                    {personnelBadge > 0 && (
                      <span
                        data-testid="nav-personnel-badge"
                        className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white"
                      >
                        {personnelBadge}
                      </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" data-testid="nav-personnel-menu" className="w-60 bg-white">
                  {personnel.map(({ to, key, icon: Icon, testid, badge }) => (
                    <DropdownMenuItem key={to} asChild>
                      <NavLink
                        to={to}
                        data-testid={`${testid}-item`}
                        className={({ isActive }) =>
                          `flex w-full cursor-pointer items-center gap-2.5 text-sm font-medium ${
                            isActive ? "text-blue-700" : "text-slate-700"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {t(`nav.${key}`)}
                        <Badge count={badge ? badgeCount[badge] : 0} testid={`${testid}-badge`} />
                      </NavLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {tail.map(({ to, key, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(`nav.${key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex min-w-0 shrink items-center gap-2">
            <NotificationBell />
            <CustomerButton />
            {isSuperAdmin(user?.role) && (
              <NavLink
                to="/superadmin"
                data-testid="nav-superadmin"
                title={t("nav.superadmin")}
                className={({ isActive }) =>
                  `hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-sm font-medium transition-colors duration-200 lg:flex ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span className="hidden xl:inline">{t("nav.superadmin")}</span>
              </NavLink>
            )}
            <HeartButton onMessage={setHeartMsg} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-900 text-xs font-semibold text-white transition-colors duration-200 hover:bg-slate-700"
                  data-testid="current-user"
                  title={`${user?.name || ""} · ${roleLabel(user?.role, t)}`}
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="user-menu" className="w-60 bg-white">
                <div className="px-2 py-1.5">
                  <p className="truncate text-sm font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{roleLabel(user?.role, t)}</p>
                </div>
                {[...MAIN_NAV, ...personnel, ...tail].map(({ to, key, icon: Icon, testid }) => (
                  <DropdownMenuItem key={to} asChild>
                    <Link
                      to={to}
                      data-testid={`${testid}-user-menu`}
                      className="flex cursor-pointer items-center gap-2.5 text-sm"
                    >
                      <Icon className="h-4 w-4 text-slate-400" /> {t(`nav.${key}`)}
                    </Link>
                  </DropdownMenuItem>
                ))}
                {isSuperAdmin(user?.role) && (
                  <DropdownMenuItem asChild>
                    <Link to="/superadmin" data-testid="nav-superadmin-user-menu" className="flex cursor-pointer items-center gap-2.5 text-sm">
                      <ShieldCheck className="h-4 w-4 text-slate-400" /> {t("nav.superadmin")}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="logout-user-menu"
                  onClick={doLogout}
                  className="flex cursor-pointer items-center gap-2.5 text-sm text-red-600"
                >
                  <LogOut className="h-4 w-4" /> {t("nav.logout", "Logga ut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              onClick={doLogout}
              className="hidden text-slate-500 hover:bg-slate-100 hover:text-slate-900 lg:inline-flex"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <button
              data-testid="mobile-menu-toggle"
              className="shrink-0 rounded-md p-2 text-slate-600 transition-colors duration-200 hover:bg-slate-100 lg:hidden"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {company?.trial_status === "trial_active" && company?.trial_days_left != null && (
          <div
            data-testid="trial-banner"
            className={`border-t px-4 py-2 text-center text-sm font-semibold ${
              company.trial_warning
                ? "border-red-200 bg-red-50 text-red-800"
                : "border-blue-200 bg-blue-50 text-blue-900"
            }`}
          >
            {company.trial_warning === "1_day"
              ? `Sista dagen av din kostnadsfria provperiod (${company.trial_days_left} dag kvar)`
              : company.trial_warning === "10_days"
                ? `Snart slut: ${company.trial_days_left} dagar kvar av din kostnadsfria provperiod`
                : `${company.trial_days_left} dagar kvar av din kostnadsfria provperiod`}
            <Link to="/abonnemang" data-testid="trial-banner-link" className="ml-2 underline">
              Välj abonnemang
            </Link>
          </div>
        )}

        {company?.trial_status === "trial_expired" && !company?.has_access && (
          <div
            data-testid="trial-expired-banner"
            className="border-t border-red-200 bg-red-50 px-4 py-2 text-center text-sm font-semibold text-red-800"
          >
            Din kostnadsfria provperiod är slut – all din data ligger kvar.
            <Link to="/abonnemang" data-testid="trial-expired-link" className="ml-2 underline">
              Teckna abonnemang för att fortsätta
            </Link>
          </div>
        )}

        {company?.demo_readonly && (
          <div
            data-testid="demo-readonly-banner"
            className="border-t border-amber-200 bg-amber-100 px-4 py-2 text-center text-sm font-semibold text-amber-900"
          >
            Demokonto i visningsläge – du kan titta på allt, men inget kan ändras eller raderas
          </div>
        )}

        {heartMsg && (
          <div
            data-testid="heart-message"
            className="flex items-center justify-center gap-2 border-t border-red-100 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700"
          >
            ❤️ {heartMsg}
            <button
              data-testid="heart-message-close"
              onClick={() => setHeartMsg("")}
              className="ml-2 rounded p-1 text-red-500 transition-colors duration-200 hover:bg-red-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {open && (
          <nav className="border-t border-slate-200 bg-white px-3 py-2 lg:hidden">
            {MAIN_NAV.map(({ to, key, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {t(`nav.${key}`)}
              </NavLink>
            ))}

            {personnel.length > 0 && (
              <div className="mt-1 border-t border-slate-100 pt-1" data-testid="nav-personnel-mobile">
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.05em] text-slate-400">
                  {t("nav.personnel")}
                </p>
                {personnel.map(({ to, key, icon: Icon, testid, badge }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`${testid}-mobile`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                        isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {t(`nav.${key}`)}
                    <Badge count={badge ? badgeCount[badge] : 0} testid={`${testid}-badge-mobile`} />
                  </NavLink>
                ))}
              </div>
            )}

            {tail.map(({ to, key, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `mt-1 flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {t(`nav.${key}`)}
              </NavLink>
            ))}
            {isSuperAdmin(user?.role) && (
              <NavLink
                to="/superadmin"
                data-testid="nav-superadmin-mobile"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `mt-1 flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <ShieldCheck className="h-4 w-4" />
                {t("nav.superadmin")}
              </NavLink>
            )}
            <button
              data-testid="logout-button-mobile"
              onClick={doLogout}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" /> {t("common.logout")}
            </button>
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-[1800px] px-4 py-5 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
